import { createColumnHelper, useReactTable } from '@tanstack/react-table';
import { secondsToMilliseconds } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { twJoin } from 'tailwind-merge';
import { useLocalStorage } from 'usehooks-ts';
import { useAccount } from 'wagmi';
import { CANCEL_ALL_ORDERS_LIMIT, TRADE_PAGE_TABLE_ROWS } from '../../constants';
import { BUTTON_LABELS } from '../../constants/buttonLabels';
import { PORTFOLIO_PAGE } from '../../constants/featureFlags';
import { PORTFOLIO_ORDERS_ROUTE_URL } from '../../constants/routes';
import { useDataForSigning } from '../../hooks/useDataForSigning';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useHandleExpiredSigner } from '../../hooks/useHandleExpiredSigner';
import { useModalState } from '../../hooks/useModalState';
import { useOrderCancelMutation } from '../../hooks/useOrderCancelMutation';
import { Order, OrderParams, useFlattenedOrders, useOpenOrders } from '../../hooks/useOrders';
import { ProductType, useProductsById } from '../../hooks/useProducts';
import { usePropsAsState } from '../../hooks/usePropAsState';
import { useActiveSelectedSubaccount } from '../../hooks/useSelectedSubaccount';
import { useTableSorting } from '../../hooks/useTableSorting';
import { useTailwindBreakpoint } from '../../hooks/useTailwindBreakpoint';
import { formatDate, formatDateRelative, formatDollar, formatNumber } from '../../lib/formatters';
import { localStorageKeys } from '../../lib/localstorageKeys';
import { getDecimalPlaces, multiply } from '../../lib/math';
import { getTradeLabel, getTriggerCondition } from '../../lib/orderHelpers';
import { toLowerCase } from '../../lib/stringHelpers';
import { cancelAllOrdersTooltip } from '../../lib/tooltips';
import { PrettyMerge } from '../../lib/tsUtilityTypes';
import { OrderSide } from '../../types/generated';
import { Button } from '../catalyst-ui-kit/button';
import { DateRange } from '../DateRangeInput/DateRangeInput';
import { Loader } from '../Loader';
import { RelativeDate } from '../RelativeDate/RelativeDate';
import {
  CommonTableProps,
  getCommonTableParams,
  getRowLeftBorderClassName,
} from '../Table/commonTableParams';
import { getMarketColumn } from '../Table/getMarketColumn';
import { StateTable } from '../Table/Table';
import { TwoRowCell } from '../Table/TwoRowCell';
import { Tooltip } from '../Tooltip/Tooltip';

const DEFAULT_SORT = { id: 'time', desc: true } as const;

// Map front-end column IDs to backend orderBy field names
const SORT_FIELD_MAP = {
  type: 'type',
  size_token0: 'quantity',
  price_with_decimals: 'price',
  time: 'createdAt',
} as const satisfies Record<string, OrderParams['orderBy']>;

type TableData = {
  ticker: string;
  displayTicker: string;
  side: Order['side'];
  type: Lowercase<Order['type']>;
  size: bigint;
  filled: bigint;
  price: bigint;
  close: boolean;
  id: string;
  reduceOnly: boolean;
  tradeLabel: string;
  time: Date;
  expiresAt: Date;
  triggerCondition: string;
  baseTokenName: string;
  status: Order['status'];
  sizeDecimals: number;
  priceDecimals: number;
};

type OpenOrdersTableUiProps = PrettyMerge<
  {
    tableData: TableData[];
    onCancelClick: (orderIds: string[]) => void;
    hasRequiredDataErrors: boolean | undefined;
    isCancellingOrderIds: string[] | undefined;
    cancelingAllOrders?: boolean;
    onCancelAll: () => void;
    hasNextPage?: boolean;
    isConnected: boolean;
    signerExpired: boolean;
  },
  CommonTableProps
>;

export const OpenOrdersTableUi: React.FC<OpenOrdersTableUiProps> = ({
  tableData,
  isCancellingOrderIds,
  hasRequiredDataErrors,
  cancelingAllOrders,
  onCancelClick,
  onCancelAll,
  hasNextPage,
  signerExpired,

  // Table props
  isConnected,
  isFetching,
  isPending,
  isError,
  pagination,
  viewAllLink,
  paginationKey,
  sorting,
  setSorting,
  fetchNextPage,
  pageSize,
  setPageSize,
  pageSizeOptions,
  className,
  style,
}) => {
  const isLg = useTailwindBreakpoint('lg');
  const showCancelAllLimitTooltip = hasNextPage || tableData.length > CANCEL_ALL_ORDERS_LIMIT;

  const isSubmitting = isCancellingOrderIds !== undefined;
  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<TableData>();

    return [
      getMarketColumn(columnHelper, {
        displaySide: !isLg,
        enableSorting: 'baseTokenName_market_side' in SORT_FIELD_MAP,
        meta: {
          accordion: {
            showInTrigger: true,
          },
        },
      }),
      columnHelper.accessor('type', {
        enableSorting: 'type' in SORT_FIELD_MAP,
        header: 'Order Type',
        meta: { cellContainerClassName: 'capitalize' },
      }),
      columnHelper.accessor(({ side, tradeLabel }) => ({ side, tradeLabel }), {
        id: 'trade_label_side',
        enableSorting: 'trade_label_side' in SORT_FIELD_MAP,
        header: 'Trade Type',
        cell: (info) => {
          const { tradeLabel, side } = info.getValue();
          return (
            <span
              className={twJoin(
                'text-nowrap',
                side === OrderSide.BUY ? 'text-contentGreen' : 'text-contentRed'
              )}
            >
              {tradeLabel}
            </span>
          );
        },
      }),
      columnHelper.accessor(
        ({ filled, baseTokenName, sizeDecimals, price }) => ({
          filled,
          baseTokenName,
          sizeDecimals,
          price,
        }),
        {
          id: 'filled_baseTokenName',
          enableSorting: 'filled_baseTokenName' in SORT_FIELD_MAP,
          header: 'Filled Size',
          meta: {
            accordion: {
              showInTrigger: true,
            },
          },
          cell: (info) => {
            const { filled, baseTokenName, sizeDecimals, price } = info.getValue();
            return (
              <TwoRowCell
                primaryVal={`${formatNumber(filled, {
                  minimumFractionDigits: sizeDecimals,
                  maximumFractionDigits: sizeDecimals,
                })} ${baseTokenName}`}
                secondaryVal={formatDollar(multiply(filled, price))}
              />
            );
          },
        }
      ),
      columnHelper.accessor(
        ({ size, baseTokenName, sizeDecimals, close, price }) => ({
          size,
          baseTokenName,
          sizeDecimals,
          close,
          price,
        }),
        {
          id: 'size_token0',
          enableSorting: 'size_token0' in SORT_FIELD_MAP,
          header: 'Total Size',
          meta: {
            accordion: {
              showInTrigger: true,
            },
          },
          cell: (info) => {
            const { size, baseTokenName, sizeDecimals, close, price } = info.getValue();
            return (
              <span className="text-nowrap">
                {close ? (
                  'Close Position'
                ) : (
                  <TwoRowCell
                    primaryVal={`${formatNumber(size, {
                      minimumFractionDigits: sizeDecimals,
                      maximumFractionDigits: sizeDecimals,
                    })} ${baseTokenName}`}
                    secondaryVal={formatDollar(multiply(size, price))}
                  />
                )}
              </span>
            );
          },
        }
      ),
      columnHelper.accessor(({ price, priceDecimals }) => ({ price, priceDecimals }), {
        id: 'price_with_decimals',
        enableSorting: 'price_with_decimals' in SORT_FIELD_MAP,
        header: 'Order Price',
        cell: (info) => {
          const { price, priceDecimals } = info.getValue();
          return (
            <span data-testid="open-orders-table-order-price">
              {formatNumber(price, {
                minimumFractionDigits: priceDecimals,
                maximumFractionDigits: priceDecimals,
              })}
            </span>
          );
        },
      }),
      columnHelper.accessor('reduceOnly', {
        enableSorting: 'reduceOnly' in SORT_FIELD_MAP,
        header: 'Reduce Only',
        cell: (info) => (info.getValue() ? 'Yes' : 'No'),
      }),
      columnHelper.accessor('triggerCondition', {
        enableSorting: 'triggerCondition' in SORT_FIELD_MAP,
        header: 'Trigger Condition',
        cell: (info) => info.getValue(),
        meta: { cellContainerClassName: 'text-nowrap' },
      }),
      columnHelper.accessor(({ time, expiresAt }) => ({ time, expiresAt }), {
        id: 'time',
        enableSorting: 'time' in SORT_FIELD_MAP,
        header: 'Time',
        cell: (info) => {
          const { time, expiresAt } = info.getValue();
          return (
            <TwoRowCell
              primaryVal={formatDate(time)}
              secondaryVal={
                <RelativeDate
                  timestamp={expiresAt}
                  formatter={(d) => `Expires ${formatDateRelative(d)}`}
                />
              }
            />
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        enableSorting: 'actions' in SORT_FIELD_MAP,
        header: () =>
          signerExpired || isPending || tableData.length === 0 ? (
            ''
          ) : (
            <Tooltip
              content={
                showCancelAllLimitTooltip ? cancelAllOrdersTooltip(CANCEL_ALL_ORDERS_LIMIT) : null
              }
            >
              <Button
                text
                size="2xs"
                color="blue"
                onClick={() => {
                  onCancelAll();
                }}
                disabled={cancelingAllOrders || isSubmitting || hasRequiredDataErrors}
                className="flex items-center gap-0.5"
              >
                {cancelingAllOrders ? <Loader className="inline-block size-3" /> : null}
                {cancelingAllOrders
                  ? BUTTON_LABELS.state.canceling
                  : BUTTON_LABELS.action.cancel}{' '}
                All
              </Button>
            </Tooltip>
          ),
        cell: (info) => {
          const orderId = info.row.original.id;
          const status = info.row.original.status;
          const isCancelingThisOrder = isCancellingOrderIds?.includes(orderId);
          return (
            <Button
              text
              size="2xs"
              color="blue"
              disabled={
                signerExpired
                  ? false
                  : isCancelingThisOrder || hasRequiredDataErrors || status === 'SUBMITTED'
              }
              onClick={() => {
                onCancelClick([orderId]);
              }}
            >
              {signerExpired ? BUTTON_LABELS.action.renewSigner : BUTTON_LABELS.action.cancel}
            </Button>
          );
        },
        meta: {
          align: 'right',
          accordion: {
            isActionColumn: true,
          },
        },
      }),
    ];
  }, [
    isLg,
    isPending,
    tableData.length,
    showCancelAllLimitTooltip,
    cancelingAllOrders,
    isSubmitting,
    hasRequiredDataErrors,
    onCancelAll,
    isCancellingOrderIds,
    onCancelClick,
    signerExpired,
  ]);

  const table = useReactTable({
    columns,
    data: tableData,
    meta: {
      getRowClassName: (row) => twJoin('lg:h-9', getRowLeftBorderClassName(row)),
    },
    ...getCommonTableParams({
      pageSize,
      sorting,
      setSorting,
    }),
  });

  return (
    <StateTable
      hasActions
      table={table}
      isPending={isPending}
      isFetching={isFetching}
      isError={isError}
      isConnected={isConnected}
      noDataText="Your orders will appear here."
      viewAllLink={viewAllLink}
      fetchNextPage={fetchNextPage}
      showPaginationControls={pagination}
      paginationKey={paginationKey}
      pageSize={pageSize}
      setPageSize={setPageSize}
      pageSizeOptions={pageSizeOptions}
      className={className}
      style={style}
    />
  );
};

export const OpenOrdersTable: React.FC<{
  products?: ProductType[];
  pagination?: boolean;
  side?: OrderSide;
  dateRange?: DateRange;
  defaultPageSize?: number;
  pageSizeOptions?: number[];
  className?: string;
  style?: React.CSSProperties;
}> = ({
  products,
  pagination,
  side,
  dateRange,
  defaultPageSize = TRADE_PAGE_TABLE_ROWS,
  pageSizeOptions = [defaultPageSize],
  className,
  style,
}) => {
  const { isConnected } = useAccount();
  const { signerExpired, withSignerCheck } = useHandleExpiredSigner();

  const { value: portfolioPageEnabled } = useFeatureFlag(PORTFOLIO_PAGE);
  const selectedSubaccount = useActiveSelectedSubaccount();

  const cancelAllOrdersModal = useModalState((state) => state.cancelAllOrdersModal);
  const setCancelAllOrdersModal = useModalState((state) => state.setCancelAllOrdersModal);

  // If the user has disabled the cancel-all confirmation modal, show a spinner while canceling
  const [showConfirmCancelAllModal] = useLocalStorage(
    localStorageKeys.showConfirmCancelAllModal,
    false
  );
  const cancelingAllOrders = showConfirmCancelAllModal && cancelAllOrdersModal === 'open';

  const { sorting, setSorting, orderBy, order } = useTableSorting(DEFAULT_SORT, SORT_FIELD_MAP);

  const [pageSize, setPageSize] = usePropsAsState(defaultPageSize);

  // Memoize useOpenOrderParams params so we can track if pagination needs to be reset
  const useOpenOrderParams = useMemo(
    () =>
      ({
        subaccountId: selectedSubaccount?.id,
        limit: 2 * pageSize,
        productIds: products?.map(({ id }) => id),
        side,
        createdAfter: dateRange?.from?.getTime(),
        createdBefore: dateRange?.to?.getTime(),
        orderBy,
        order,
      }) as const,
    [
      dateRange?.from,
      dateRange?.to,
      order,
      orderBy,
      pageSize,
      products,
      selectedSubaccount?.id,
      side,
    ]
  );

  const {
    data: orderData,
    hasNextPage,
    isPending,
    isFetching,
    isError,
    isFetchingNextPage,
    fetchNextPage,
  } = useOpenOrders(useOpenOrderParams);
  const orders = useFlattenedOrders(orderData?.pages);

  const productsById = useProductsById();
  const { error } = useDataForSigning();

  const cancelMutation = useOrderCancelMutation();
  const tableData = useMemo(() => {
    if (!orders) return [];

    return orders.map((order) => {
      const product = productsById?.[order.productId];
      const priceDecimals = getDecimalPlaces(product?.tickSize || 0n);
      return {
        id: order.id,
        ticker: product?.ticker || '',
        displayTicker: product?.displayTicker || '',
        type: toLowerCase(order.type),
        side: order.side,
        size: order.quantity,
        filled: order.filled,
        price: order.price,
        close: order.close,
        baseTokenName: product?.baseTokenName || '',
        reduceOnly: order.reduceOnly,
        tradeLabel: getTradeLabel(order.side, order.reduceOnly),
        time: new Date(order.createdAt),
        expiresAt: new Date(secondsToMilliseconds(order.expiresAt)),
        triggerCondition: getTriggerCondition(order, priceDecimals),
        status: order.status,
        sizeDecimals: getDecimalPlaces(product?.lotSize || 0n),
        priceDecimals,
      };
    });
  }, [orders, productsById]);

  const handleCancelClick = useCallback(
    (orderIds: string[]) => withSignerCheck(cancelMutation.mutate)(orderIds),
    [cancelMutation, withSignerCheck]
  );

  const handleCancelAll = useCallback(() => {
    setCancelAllOrdersModal('open');
  }, [setCancelAllOrdersModal]);

  return (
    <OpenOrdersTableUi
      tableData={tableData}
      hasRequiredDataErrors={Boolean(error)}
      isCancellingOrderIds={cancelMutation.isPending ? cancelMutation.variables : undefined}
      isPending={isPending || !productsById}
      isFetching={isFetching || isFetchingNextPage}
      isError={isError}
      isConnected={isConnected}
      signerExpired={signerExpired}
      hasNextPage={hasNextPage}
      pagination={pagination}
      paginationKey={useOpenOrderParams}
      sorting={sorting}
      setSorting={setSorting}
      onCancelClick={handleCancelClick}
      cancelingAllOrders={cancelingAllOrders}
      onCancelAll={handleCancelAll}
      pageSize={pageSize}
      setPageSize={setPageSize}
      pageSizeOptions={pagination ? pageSizeOptions : [pageSize]}
      fetchNextPage={fetchNextPage}
      viewAllLink={portfolioPageEnabled && !pagination ? PORTFOLIO_ORDERS_ROUTE_URL : undefined}
      className={className}
      style={style}
    />
  );
};
